import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, Crown, Sparkles, Rocket, Ticket, XCircle, Zap, Sprout, Cloud } from "lucide-react";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import FreeTrialBadge from "@/components/FreeTrialBadge";
import useSEO from "@/hooks/useSEO";

const ICONS = { free: Sprout, creator: Rocket, pro: Crown, power: Zap };

// Map plan-feature description lines → known feature flag key. The match is
// substring-based on the lowercased line so the same logic works across the
// six translated descriptions ("AI videos/month", "videos/mes", "videolar/ay"
// etc). When the matched flag is `hidden` we drop the line; when it is
// `coming_soon` we still show it but mute it; when it is `new` we tag NEW.
const FEATURE_KEYWORDS = {
  video_producer: ["video", "vidéo", "vídeo", "videolar", "sora"],
  interview_practice: ["interview", "mülakat", "entrevista", "entretien", "kolloquium", "colloquio"],
  chat: ["chat", "sohbet"],
  gpt5_smart_router: ["gpt-5", "gpt 5", "gpt5"],
  opus_master: ["opus 4.5", "claude opus", "master mode"],
  cv_chat: ["cv chat", "cv upload", "cv sohbeti", "cv-chat", "chat de cv"],
};

export default function Pricing() {
  const { t } = useT();
  const { user, refresh } = useAuth();
  const { isHidden, isComingSoon, isNew } = useFeatureFlags();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState("free");
  const [unlimited, setUnlimited] = useState(false);
  const [sfProActive, setSfProActive] = useState(false);
  const [sfProExpiresAt, setSfProExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [period, setPeriod] = useState("monthly");
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useSEO({
    title: "Pricing — Lumioos AI Workspace Plans (Free, Creator, Pro, Power)",
    description: "Compare Lumioos plans: Free (100 credits/mo), Creator $9, Pro $29, Power $79. Includes GPT-5, Claude Opus 4.5, Sora 2 video, image gen and CV builder.",
    canonical: "https://mylumioos.com/pricing",
  });

  // Decide whether a plan-feature line should be shown / dimmed / NEW-tagged
  // based on the admin's feature-flag matrix. Lines that don't match any
  // known feature pass through untouched.
  //   hidden      → drop the line entirely
  //   coming_soon → keep visible, muted, with a "Soon" badge
  //   new         → keep visible, "NEW" badge
  const annotateFeature = (line) => {
    const lc = (line || "").toLowerCase();
    for (const [key, words] of Object.entries(FEATURE_KEYWORDS)) {
      if (words.some((w) => lc.includes(w))) {
        if (isHidden(key)) return { line, hidden: true };
        if (isComingSoon(key)) return { line, comingSoon: true };
        if (isNew(key)) return { line, isNew: true };
        return { line };
      }
    }
    return { line };
  };

  const cancelSubscription = async () => {
    if (!window.confirm(t("cancel_confirm"))) return;
    setCancelling(true);
    try {
      const { data } = await api.post("/billing/cancel");
      // Friendly localised success — ignore raw HTTP message from backend
      let endStr = "";
      if (data?.plan_expires_at) {
        try { endStr = new Date(data.plan_expires_at).toLocaleDateString(); } catch { /* ignore */ }
      }
      toast.success(endStr ? t("cancel_ok_with_date", { date: endStr }) : t("cancel_ok"), {
        duration: 6000,
      });
      if (refresh) await refresh();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("cancel_fail"));
    } finally {
      setCancelling(false);
    }
  };

  const load = () => {
    setLoading(true);
    // Authenticated users hit /billing/plans (returns their current plan +
    // admin flags). Anonymous visitors landing on the public /pricing page
    // get the lighter /billing/plans-public payload — no 401, no redirect
    // out of the page.
    const endpoint = user ? "/billing/plans" : "/billing/plans-public";
    api.get(endpoint)
      .then((r) => {
        setPlans(r.data.plans || []);
        setCurrent(r.data.current_plan || "free");
        setUnlimited(!!r.data.is_admin_unlimited);
        setSfProActive(!!r.data.sf_pro_active);
        setSfProExpiresAt(r.data.sf_pro_expires_at || null);
      })
      .catch(() => toast.error(t("err_generic")))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const [discountCode, setDiscountCode] = useState(() => {
    // Support both `?code=` (legacy admin share links) and `?promo=` (used by
    // the trial-expiry recovery modal). Uppercased so backend lookup matches.
    const params = new URLSearchParams(window.location.search);
    return (params.get("code") || params.get("promo") || "").toUpperCase();
  });

  const subscribe = async (planId) => {
    if (planId === "free" || (planId === current && !unlimited)) return;
    setBuying(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        period,
        origin_url: window.location.origin,
        discount_code: discountCode.trim() || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
      setBuying(null);
    }
  };

  const redeem = async (e) => {
    e?.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) { toast.error(t("redeem_required")); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/billing/redeem", { code: c });
      toast.success(`${t("redeem_success")} · ${data.plan_id.toUpperCase()} · ${data.months_added} ${t("pricing_month")}`);
      setCode("");
      setRedeemOpen(false);
      await refresh();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return <div className="p-10 flex items-center justify-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  return (
    <div data-testid="pricing-page">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-600">{t("pricing_eyebrow")}</div>
      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("pricing_h1")}</h1>
      <p className="mt-2 text-zinc-600 max-w-2xl">{t("pricing_sub")}</p>
      {/* Free trial offer — hidden when admin disables auto-trial */}
      {current === "free" && (
        <div className="mt-4">
          <FreeTrialBadge size="md" />
        </div>
      )}

      {/* Current status */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
          <Crown className="w-3.5 h-3.5 text-amber-600" />
          <span className="text-xs font-semibold text-amber-800">
            {t("pricing_current")}: <span className="font-black uppercase">{current === "admin" ? (user?.plan || "free") : current}</span>
            {user?.plan_expires_at && (current === "creator" || current === "pro") && (
              <span className="ml-1.5 font-normal">· {t("pricing_expires")} {new Date(user.plan_expires_at).toLocaleDateString()}</span>
            )}
          </span>
        </div>

        {sfProActive && (
          <div
            data-testid="sf-pro-active-badge"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white shadow-sm"
            style={{ background: "linear-gradient(90deg, #00A1E0 0%, #009dde 100%)" }}
          >
            <Cloud className="w-3.5 h-3.5" />
            <span className="text-xs font-bold uppercase tracking-wider">Salesforce Pro Active</span>
            {sfProExpiresAt && (
              <span className="text-[10px] font-normal opacity-90">· till {new Date(sfProExpiresAt).toLocaleDateString()}</span>
            )}
          </div>
        )}

        <button onClick={() => setRedeemOpen((v) => !v)} data-testid="pricing-redeem-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-pink-200 bg-white hover:bg-pink-50 rounded-md text-xs font-bold uppercase tracking-wider text-pink-700">
          <Ticket className="w-3.5 h-3.5" />
          {t("redeem_have_code")}
        </button>

        {(current === "creator" || current === "pro") && !unlimited && (
          <button
            onClick={cancelSubscription}
            disabled={cancelling}
            data-testid="pricing-cancel-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-red-200 bg-white hover:bg-red-50 rounded-md text-xs font-bold uppercase tracking-wider text-red-700 disabled:opacity-60"
            title={t("cancel_tooltip")}
          >
            {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
            {t("cancel_btn")}
          </button>
        )}
      </div>

      {redeemOpen && (
        <form onSubmit={redeem} className="mt-3 inline-flex flex-wrap items-center gap-2 p-3 bg-white border border-zinc-200 rounded-md" data-testid="redeem-form">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="WELCOME50" maxLength={64}
            data-testid="redeem-input"
            className="px-3 py-2 border border-zinc-300 rounded text-sm font-mono uppercase w-48" />
          <button type="submit" disabled={redeeming} data-testid="redeem-submit"
            className="btn-accent inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded">
            {redeeming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            {t("redeem_apply")}
          </button>
        </form>
      )}

      {/* Period toggle */}
      <div className="mt-8 inline-flex items-center p-1 bg-zinc-100 rounded-lg" data-testid="pricing-period-toggle">
        <button onClick={() => setPeriod("monthly")} data-testid="pricing-period-monthly"
          className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${period === "monthly" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600"}`}>
          {t("pricing_monthly")}
        </button>
        <button onClick={() => setPeriod("yearly")} data-testid="pricing-period-yearly"
          className={`px-4 py-2 text-sm font-bold rounded-md transition-all relative ${period === "yearly" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600"}`}>
          {t("pricing_yearly")}
          {(() => {
            // Compute yearly savings dynamically from actual monthly vs yearly
            // prices on the currently-loaded plans. Uses the biggest saving
            // across paid plans so the label is truthful ("save up to N%")
            // no matter which admin-configured price the user is comparing.
            // Falls back to hiding the badge if plans aren't loaded yet or
            // if no plan offers a positive yearly discount.
            const paid = (plans || []).filter(
              (pl) => pl && pl.id !== "free" && Number(pl.monthly_price) > 0 && Number(pl.yearly_price) > 0
            );
            let bestPct = 0;
            for (const pl of paid) {
              const monthlyYearly = Number(pl.monthly_price) * 12;
              const savings = monthlyYearly - Number(pl.yearly_price);
              if (savings > 0) {
                const pct = (savings / monthlyYearly) * 100;
                if (pct > bestPct) bestPct = pct;
              }
            }
            if (bestPct < 1) return null;
            return (
              <span
                data-testid="pricing-yearly-discount-badge"
                className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider rounded bg-emerald-500 text-white shadow-sm"
              >
                -{Math.round(bestPct)}%
              </span>
            );
          })()}
        </button>
      </div>

      {/* Discount code input — optional */}
      <div className="mt-4 flex items-center justify-center gap-2">
        <input
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
          data-testid="pricing-discount-input"
          placeholder={t("pricing_discount_placeholder") || "Have a code?"}
          className="w-44 px-3 py-1.5 border border-zinc-300 rounded text-sm font-mono text-center"
        />
        {discountCode && (
          <span className="text-xs font-bold text-fuchsia-600">✓ {t("pricing_discount_applied") || "will apply at checkout"}</span>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.filter((p) => p.id !== "sf_pro_addon").map((p) => {
          const Icon = ICONS[p.id] || Sparkles;
          const isCurrent = (current === p.id) || (current === "admin" && (user?.plan || "free") === p.id);
          const isCreator = p.id === "creator";
          const isPro = p.id === "pro";
          const isPower = p.id === "power";
          const highlight = isCreator || isPro || isPower;
          const price = period === "yearly" ? p.yearly_price : p.monthly_price;
          const perLabel = period === "yearly" ? t("pricing_year") : t("pricing_month");
          // Tier-themed card colors. Creator = silver, Pro = gold, Power = purple.
          const cardThemeClasses = isPower
            ? "border-purple-400 bg-gradient-to-br from-purple-50/70 via-fuchsia-50 to-purple-100/50 shadow-xl shadow-purple-300/40 ring-1 ring-purple-300/40"
            : isPro
            ? "border-amber-400 bg-gradient-to-br from-amber-50/60 via-yellow-50 to-amber-100/40 shadow-xl shadow-amber-200/40 ring-1 ring-amber-300/40"
            : isCreator
            ? "border-zinc-400 bg-gradient-to-br from-zinc-50 via-slate-50 to-zinc-100 shadow-lg shadow-zinc-200/40 ring-1 ring-zinc-300/40"
            : "border-zinc-200 bg-white";
          const iconClass = isPower ? "text-purple-600" : isPro ? "text-amber-600" : isCreator ? "text-zinc-500" : "text-emerald-600";
          const tierLabel = isPower ? "POWER" : isPro ? "PRO" : isCreator ? "CREATOR" : null;
          return (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`relative border-2 p-7 flex flex-col rounded-xl ${cardThemeClasses}`}>
              {tierLabel && (
                <div className={`absolute -top-3 left-7 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded ${
                  isPower
                    ? "bg-gradient-to-r from-purple-600 via-fuchsia-600 to-purple-700 text-white shadow-md shadow-purple-500/40"
                    : isPro
                    ? "bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-zinc-900 shadow-md shadow-amber-500/30"
                    : "bg-gradient-to-r from-zinc-400 via-slate-300 to-zinc-500 text-zinc-900 shadow-sm"
                }`}>
                  {isPower ? `⚡ ${tierLabel}` : isPro ? `★ ${tierLabel}` : tierLabel}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Icon className={`w-5 h-5 ${iconClass}`} />
                <h3 className="text-xl font-black tracking-tight">{p.name}</h3>
              </div>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-4xl font-black tracking-tighter">${Number(price).toFixed(Math.abs(price - Math.round(price)) > 0.005 ? 2 : 0)}</span>
                <span className="text-zinc-500 text-sm">/{perLabel}</span>
              </div>
              {period === "yearly" && p.id !== "free" && p.monthly_price > 0 && (
                <div className="mt-1 text-xs text-emerald-600 font-bold">
                  {t("pricing_yearly_savings", { saved: Math.max(0, (p.monthly_price * 12 - p.yearly_price)).toFixed(0) })}
                </div>
              )}
              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features
                  .map(annotateFeature)
                  .filter((it) => !it.hidden)
                  .map((it, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${it.comingSoon ? "text-zinc-400" : "text-zinc-700"}`}>
                      <Check className={`w-4 h-4 shrink-0 mt-0.5 ${it.comingSoon ? "text-zinc-300" : "text-orange-600"}`} />
                      <span className="flex-1">{it.line}</span>
                      {it.comingSoon && (
                        <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600 shrink-0">Soon</span>
                      )}
                      {it.isNew && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500 text-white shrink-0">
                          <Zap className="w-2.5 h-2.5" /> New
                        </span>
                      )}
                    </li>
                  ))}
              </ul>
              {/* AI Models Included — pills matching Landing.js style */}
              <div className="mt-5 pt-4 border-t border-zinc-200/60">
                <div className={`text-[10px] font-bold uppercase tracking-[0.2em] mb-2.5 ${
                  isPower ? "text-purple-700" : isPro ? "text-amber-700" : isCreator ? "text-zinc-600" : "text-emerald-700"
                }`}>
                  AI Models Included
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(() => {
                    // Build the model badge list by filtering each model with its admin flag.
                    // Hidden → drop, coming_soon → render with SOON badge, others → normal.
                    const modelDefs = [
                      { name: "Gemini 3 Flash", flag: "chat_quick_model", forPlan: ["free","creator","pro","power"] },
                      { name: "Claude Sonnet 4.6", flag: "chat_smart_model", forPlan: ["creator","pro","power"] },
                      { name: "GPT-5", flag: "gpt5_smart_router", forPlan: ["pro","power"] },
                      { name: "Opus 4.5", flag: "opus_master", forPlan: ["pro","power"] },
                      { name: "GPT 5.6 Sol",   flag: "gpt56_sol",   forPlan: ["pro","power"] },
                      { name: "GPT 5.6 Terra", flag: "gpt56_terra", forPlan: ["pro","power"] },
                      { name: "GPT 5.6 Luna",  flag: "gpt56_luna",  forPlan: ["pro","power"] },
                    ];
                    const items = modelDefs
                      .filter((md) => md.forPlan.includes(p.id))
                      .filter((md) => !md.flag || !isHidden(md.flag))
                      .map((md) => ({ name: md.name, soon: md.flag ? isComingSoon(md.flag) : false }));
                    return items.map((m, i) => {
                      const isGpt5 = m.name === "GPT-5";
                      const isOpus = m.name === "Opus 4.5";
                      const isSol = m.name === "GPT 5.6 Sol";
                      const isTerra = m.name === "GPT 5.6 Terra";
                      const isLuna = m.name === "GPT 5.6 Luna";
                      return (
                        <span key={i}
                          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded ${
                            isOpus
                              ? "bg-gradient-to-r from-violet-500 via-fuchsia-500 to-purple-600 text-white shadow-sm shadow-fuchsia-300/50"
                              : isGpt5
                              ? "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-zinc-900 shadow-sm shadow-amber-300/50"
                              : isSol
                              ? "bg-gradient-to-r from-orange-400 via-rose-400 to-red-500 text-white shadow-sm shadow-orange-300/50"
                              : isTerra
                              ? "bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-sm shadow-teal-300/50"
                              : isLuna
                              ? "bg-gradient-to-r from-indigo-500 via-blue-500 to-sky-600 text-white shadow-sm shadow-blue-300/50"
                              : "bg-white border border-zinc-300 text-zinc-700"
                          } ${m.soon ? "opacity-60" : ""}`}>
                          {isOpus ? "✨ " : isGpt5 ? "👑 " : isSol ? "🌞 " : isTerra ? "🌍 " : isLuna ? "🌙 " : ""}{m.name}
                          {m.soon && (
                            <span className="ml-0.5 text-[8.5px] font-black uppercase tracking-wider px-1 py-0.5 rounded bg-zinc-200 text-zinc-600">
                              Soon
                            </span>
                          )}
                        </span>
                      );
                    });
                  })()}
                </div>
              </div>
              <button onClick={() => subscribe(p.id)} disabled={isCurrent || p.id === "free" || !!buying}
                data-testid={`pricing-cta-${p.id}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-md text-sm transition-all ${
                  isCurrent
                    ? "bg-zinc-100 text-zinc-500 cursor-not-allowed"
                    : p.id === "free"
                    ? "border border-zinc-300 text-zinc-700 cursor-default"
                    : highlight
                    ? "btn-accent"
                    : "bg-zinc-950 hover:bg-zinc-800 text-white"
                }`}>
                {buying === p.id ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t("pricing_redirecting")}</>
                ) : isCurrent ? (
                  t("pricing_current_plan")
                ) : p.id === "free" ? (
                  t("pricing_free_default")
                ) : (
                  <>{t("pricing_subscribe")} · ${Number(price).toFixed(Math.abs(price - Math.round(price)) > 0.005 ? 2 : 0)}/{period === "yearly" ? t("pricing_year_short") : t("pricing_month_short")}</>
                )}
              </button>
            </div>
          );
        })}
      </div>

          {/* Salesforce Pro Add-on — removed 2026-07-28. The section is now
              hard-disabled regardless of what /billing/plans-public returns.
              We leave the enclosing IIFE so nothing else has to move. */}
          {(() => {
            return null;
            // eslint-disable-next-line no-unreachable
            const sfPlan = plans.find((p) => p.id === "sf_pro_addon");
            if (!sfPlan) return null;
            const price = period === "yearly" ? sfPlan.yearly_price : sfPlan.monthly_price;
            const perLabel = period === "yearly" ? t("pricing_year") : t("pricing_month");
            const isPaidUser = unlimited || current === "creator" || current === "pro";
            return (
              <div className="mt-10 relative" data-testid="sf-pro-addon-section">
                <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-sky-700 mb-3">
                  <Sparkles className="w-3 h-3 inline-block mr-1" /> Enterprise Add-on · Requires Pro or Creator
                </div>
              <div
                className="relative border-2 rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  borderColor: "#00A1E0",
                  background: "linear-gradient(135deg, #001a33 0%, #003e6b 45%, #00A1E0 100%)",
                }}
              >
                {/* subtle cloud-pattern overlay via CSS */}
                <div className="absolute inset-0 opacity-[0.04]" style={{
                  backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)",
                  backgroundSize: "40px 40px, 60px 60px",
                }} />
                <div className="relative grid md:grid-cols-5 gap-6 p-7 md:p-10 text-white">
                  <div className="md:col-span-3">
                    <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-white/15 border border-white/25 text-[10px] font-black uppercase tracking-wider">
                      <Cloud className="w-3 h-3" /> Salesforce Cloud
                    </div>
                    <h2 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">{sfPlan.name}</h2>
                    <p className="mt-2 text-sky-100/90 text-sm max-w-md">
                      Hire a senior Salesforce developer on autopilot. Apex, LWC, Aura, Flows, Permission Sets, Validation Rules — built and deployed from chat.
                    </p>
                    <ul className="mt-5 space-y-2 max-w-md">
                      {sfPlan.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-white/95">
                          <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#7BD5FF" }} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-2 flex flex-col justify-center">
                    <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-5">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-sky-200">{perLabel === t("pricing_year") ? "Billed yearly" : "Billed monthly"}</div>
                      <div className="mt-1.5 flex items-baseline gap-1.5">
                        <span className="text-5xl font-black tracking-tighter">${Number(price).toFixed(0)}</span>
                        <span className="text-sky-100 text-base">/{perLabel}</span>
                      </div>
                      {period === "yearly" && sfPlan.monthly_price > 0 && (
                        <div className="mt-1 text-xs font-bold" style={{ color: "#A8F0CB" }}>
                          Save ${Math.max(0, (sfPlan.monthly_price * 12 - sfPlan.yearly_price)).toFixed(0)} / year
                        </div>
                      )}
                      {!isPaidUser && !sfProActive && (
                        <div className="mt-3 rounded-md bg-amber-400/15 border border-amber-300/40 px-3 py-2 text-[11px] text-amber-100">
                          <strong className="text-amber-200">Requires Creator or Pro plan.</strong> Upgrade your base subscription first.
                        </div>
                      )}
                      <button
                        onClick={() => isPaidUser ? subscribe(sfPlan.id) : document.querySelector('[data-testid="pricing-cta-pro"]')?.scrollIntoView({behavior: "smooth", block: "center"})}
                        disabled={sfProActive || !!buying}
                        data-testid="pricing-cta-sf_pro_addon"
                        className="mt-4 w-full inline-flex items-center justify-center gap-2 font-bold py-3 px-4 rounded-md text-sm transition-all"
                        style={
                          sfProActive
                            ? { background: "rgba(255,255,255,0.15)", color: "#cfe9ff", cursor: "not-allowed" }
                            : !isPaidUser
                            ? { background: "rgba(255,255,255,0.2)", color: "#ffffff" }
                            : { background: "#ffffff", color: "#003e6b" }
                        }
                      >
                        {buying === sfPlan.id ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> {t("pricing_redirecting")}</>
                        ) : sfProActive ? (
                          <><Check className="w-4 h-4" /> Active</>
                        ) : !isPaidUser ? (
                          <>Upgrade base plan first ↑</>
                        ) : (
                          <>Activate Salesforce Pro</>
                        )}
                      </button>
                      <div className="mt-2 text-[10px] text-sky-200/80 text-center">Cancel anytime · Stripe checkout</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Mobile pricing disclosure — sits directly under the plan cards so
          web visitors comparing prices know the iOS/Android app charges more
          (Apple/Google take 30% commission). Nudges high-margin web signups
          without discouraging App Store subscribers. */}
      <div
        className="mt-8 max-w-3xl mx-auto rounded-lg border border-sky-200 bg-sky-50/60 px-4 py-3 flex items-start gap-3"
        data-testid="pricing-mobile-disclaimer"
      >
        <svg className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <circle cx="12" cy="16" r="0.5" fill="currentColor" />
        </svg>
        <div className="text-[13px] leading-relaxed text-sky-900">
          <span className="font-semibold">{t("pricing_mobile_note_title")}</span>{" "}
          {t("pricing_mobile_note_body")}
        </div>
      </div>

      {/* "Why is Pro $29?" FAQ — pre-empts pricing-shock churn after the
          $10→$29 bump by showing transparent value math. Industry standard
          retention tactic for SaaS pricing changes. */}
      <div className="mt-12 max-w-3xl">
        <h2 className="text-lg sm:text-xl font-black tracking-tight mb-3">{t("pricing_faq_title")}</h2>
        <details className="border border-zinc-200 rounded-lg p-4 bg-white mb-2" data-testid="pricing-faq-why-29">
          <summary className="font-bold text-sm cursor-pointer text-zinc-900">{t("pricing_faq_why_pro_q")}</summary>
          <div className="mt-3 text-sm text-zinc-700 leading-relaxed">
            {t("pricing_faq_why_pro_a")}
          </div>
        </details>
        <details className="border border-zinc-200 rounded-lg p-4 bg-white mb-2" data-testid="pricing-faq-cancel">
          <summary className="font-bold text-sm cursor-pointer text-zinc-900">{t("pricing_faq_cancel_q")}</summary>
          <div className="mt-3 text-sm text-zinc-700 leading-relaxed">{t("pricing_faq_cancel_a")}</div>
        </details>
        <details className="border border-zinc-200 rounded-lg p-4 bg-white mb-2" data-testid="pricing-faq-existing">
          <summary className="font-bold text-sm cursor-pointer text-zinc-900">{t("pricing_faq_existing_q")}</summary>
          <div className="mt-3 text-sm text-zinc-700 leading-relaxed">{t("pricing_faq_existing_a")}</div>
        </details>
      </div>

      <div className="mt-10 text-xs text-zinc-500 max-w-2xl">
        {t("pricing_disclaimer")}
      </div>
    </div>
  );
}
